// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import XngZone from "@ces/App/Components/Common/XngZone";
import xHeaderUpdate from "@ces/App/Handlers/XNG/Header";
import useSize from "@ces/App/Hooks/useSize";
import { xSiteMessage } from "@ces/XDL/Utilities";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetDrawerIsOpen";
import { getIsPunchOutSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import NavigationModeMenu, {
    NavigationModeCookieName,
    NavigationModeMenuStyles,
} from "@insite/content-library/Components/NavigationModeMenu";
import ShowInventoryToggle, { ShowInventoryToggleStyles } from "@insite/content-library/Components/ShowInventoryToggle";
import ShowPricingToggle, { ShowPricingToggleStyles } from "@insite/content-library/Components/ShowPricingToggle";
import Hidden from "@insite/mobius/Hidden";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import getContrastColor from "@insite/mobius/utilities/getContrastColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import * as cssLinter from "css";
import React, { FC, useEffect, useRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    customCSS = "customCSS",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.customCSS]: string;
        [fields.showPricingToggle]: boolean;
        [fields.showInventoryToggle]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    enableWarehousePickup: getSettingsCollection(state).accountSettings.enableWarehousePickup,
    displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
    currentMode: getCookie(NavigationModeCookieName) || "Storefront",
});

// XNG-Changes
const mapDispatchToProps = {
    xHeaderUpdate,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const Navigation = styled.div`
    color: ${getContrastColor("common.accent")};
    font-size: 15px;
    background-color: ${getColor("common.accent")};
    padding: 0 45px 0 45px;
    height: 50px;
    text-transform: uppercase;
    display: flex;
    justify-content: flex-end;
    align-items: center;

    // XNG-Changes
    ${injectCss}
`;

const NavItem = styled.span<InjectableCss>`
    display: inline-flex;

    > span,
    > a {
        margin-left: 10px;
    }
    ${injectCss}
`;

export interface SecondaryNavigationStyles {
    wrapper?: InjectableCss;
    navigationModeMenu?: NavigationModeMenuStyles;
    showInventoryLink?: InjectableCss;
    showInventoryToggle?: ShowInventoryToggleStyles;
    showPricingLink?: InjectableCss;
    showPricingToggle?: ShowPricingToggleStyles;
    currencyLink?: InjectableCss;
    languageLink?: InjectableCss;
    warehousePickupLink?: InjectableCss;
    signInLink?: InjectableCss;
    // XNG-Changes
    navigation?: InjectableCss;
    container?: InjectableCss;
    links?: InjectableCss;
    rightSide?: InjectableCss;
}

export const secondaryNavigationStyles: SecondaryNavigationStyles = {
    showInventoryLink: {
        css: css`
            margin-left: 10px;
        `,
    },
    showPricingLink: {
        css: css`
            margin-left: 10px;
        `,
    },
    currencyLink: {
        css: css`
            margin-left: 16px;
        `,
    },
    languageLink: {
        css: css`
            flex: 0 0 auto;
            margin-left: 16px;
        `,
    },
    warehousePickupLink: {
        css: css`
            flex: 0 1 auto;
            margin-left: 6px;
            min-width: 30px;
        `,
    },
    signInLink: {
        css: css`
            flex: 0 0 auto;
            margin-left: 6px;
        `,
    },
};

const defaultCustomCss = `.wrapper {
}

.navigation-mode-menu {
}

.currency-menu {
}

.language-menu {
}

.ship-to-address-menu {
}

.sign-in-link {
}
.header-message {
}
`;

const SecondaryNavigation: FC<Props> = ({
    id,
    enableWarehousePickup,
    displayPricingAndInventory,
    currentMode,
    fields,
    parentId,
    // XNG-Changes
    xHeaderUpdate,
}) => {
    const styles = useMergeStyles("secondaryNavigation", secondaryNavigationStyles);
    const secondaryNavigationHeaderRef = useRef<HTMLDivElement>(null);
    const secondaryNavigationHeaderHeight = useSize(secondaryNavigationHeaderRef)?.height;

    useEffect(() => {
        xHeaderUpdate({ key: "secondaryNavigationHeaderHeight", value: `${secondaryNavigationHeaderHeight ?? 0}px` });
    }, [secondaryNavigationHeaderHeight]);

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper} ref={secondaryNavigationHeaderRef}>
            <Hidden below="lg">
                <StyledWrapper className="container" {...styles.container}>
                    <StyledWrapper {...styles.wrapper}>
                        {/* <Navigation className="navigation-wrapper" {...styles.navigation}> */}
                        {/* <NavItem className="navigation-mode-menu"> */}
                        {/*    <NavigationModeMenu extendedStyles={styles.navigationModeMenu} /> */}
                        {/* </NavItem> */}
                        {/* {fields.showInventoryToggle && displayPricingAndInventory && currentMode === "Storefront" && ( */}
                        {/*    <NavItem
                                    className="show-inventory-toggle"
                                    data-test-selector="showInventoryToggle"
                                    {...styles.showInventoryLink}
                                > 
                        */}
                        {/*        <ShowInventoryToggle extendedStyles={styles.showInventoryToggle} /> */}
                        {/*    </NavItem> */}
                        {/* )} */}
                        {/* {fields.showPricingToggle && displayPricingAndInventory && currentMode === "Storefront" && ( */}
                        {/*    <NavItem
                                    className="show-pricing-toggle"
                                    data-test-selector="showPricingToggle"
                                    {...styles.showPricingLink}
                                >
                        */}
                        {/*        <ShowPricingToggle extendedStyles={styles.showPricingToggle} /> */}
                        {/*    </NavItem> */}
                        {/* )} */}
                        {/* <NavItem className="currency-menu" {...styles.currencyLink}> */}
                        {/*    <Zone zoneName="Currency" contentId={id} fixed /> */}
                        {/* </NavItem> */}
                        {/* <NavItem className="language-menu" {...styles.languageLink}> */}
                        {/*    <Zone zoneName="Language" contentId={id} fixed /> */}
                        {/* </NavItem> */}
                        {/* {enableWarehousePickup && ( */}
                        {/*    <NavItem className="ship-to-address-menu" {...styles.warehousePickupLink}> */}
                        {/*        <Zone zoneName="ShipToAddress" contentId={id} fixed /> */}
                        {/*    </NavItem> */}
                        {/* )} */}
                        {/* <NavItem className="sign-in-link" {...styles.signInLink}> */}
                        {/*    <Zone zoneName="SignIn" contentId={id} fixed /> */}
                        {/* </NavItem> */}
                        {/* <NavItem className="links" {...styles.links}> */}
                        {/* <XngZone zoneName="Links" hint="Links" contentId={id} /> */}
                        {/* </NavItem> */}
                        {/* </Navigation> */}

                        <StyledWrapper className="links" {...styles.links}>
                            {createWidgetElement("Common/HeaderShipToAddress", {
                                fields: {},
                            })}
                            <XngZone zoneName="Links" hint="Links" contentId={id} />
                        </StyledWrapper>
                        <StyledWrapper className="header-message" {...styles.rightSide}>
                            <XngZone hint="Spot" contentId={parentId} zoneName="Spot" />
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </Hidden>
        </StyledWrapper>
    );
};

const secondaryNavigation: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SecondaryNavigation),
    definition: {
        group: "Common",
        icon: "link-simple",
        fieldDefinitions: [
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            // XNG-Change
            // {
            //     name: fields.showPricingToggle,
            //     displayName: "Show/Hide Pricing Toggle",
            //     editorTemplate: "CheckboxField",
            //     defaultValue: false,
            //     fieldType: "General",
            // },
            // {
            //     name: fields.showInventoryToggle,
            //     displayName: "Show/Hide Inventory Toggle",
            //     editorTemplate: "CheckboxField",
            //     defaultValue: false,
            //     fieldType: "General",
            // },
        ],
    },
};

export default secondaryNavigation;
